import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { Flex } from "src/components/Flex.tsx";
import { OrderPosition } from "./OrderPosition/OrderPosition.tsx";
import * as styles from "./OrderPreview.style.ts";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { H3, H4 } from "src/components/HeadingV2/HeadingV2.tsx";
import { formatPrice } from "@exonn/utils";
import { Currency } from "src/common/constants.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useExonnHubOrder } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubOrder.ts";
import type { ExonnHubCartModel } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.types.ts";
import EmptyOrderStateImage from "src/assets/illustrations/empty_postion.svg?react";
import { useIsAddingProductToCart } from "src/admin/modules/b2b-gate-order/components/EditSupplierOrderForB2bGate/hooks/useIsAddingProductToCart.ts";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { useExonnHubContext } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubContext.ts";

export const OrderPreview = () => {
  const { orderId } = useExonnHubContext();
  const { data: order, isLoading } = useExonnHubOrder({
    orderId,
  });

  return (
    <Flex flexDirection="column" css={styles.root}>
      {isLoading ? (
        "loading..."
      ) : order?.items.length ? (
        <OrderPreviewNotEmptyOrder order={order} />
      ) : (
        <OrderPreviewEmptyCart />
      )}
    </Flex>
  );
};

const OrderPreviewEmptyCart = () => {
  const { setIsOrderPreviewOpen } = useExonnHubStore();
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  return (
    <Flex flexDirection="column">
      <div css={styles.emptyStateContainer}>
        <H4>{t("orderPreview.emptyState.title")}</H4>
        <div css={styles.emptyOrderStateImage}>
          <EmptyOrderStateImage />
        </div>
      </div>
      <Flex justifyContent="center" css={styles.footer}>
        <ButtonV2
          onClick={() => setIsOrderPreviewOpen(false)}
          skin={"info"}
          variant={"filled"}
        >
          {t("orderPreview.emptyState.action")}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};

const OrderPreviewNotEmptyOrder = ({ order }: { order: ExonnHubCartModel }) => {
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  const { setIsOrderPreviewOpen } = useExonnHubStore();
  const isAddingProductToCart = useIsAddingProductToCart();

  return (
    <>
      <LoadingOverlay.Root isLoading={isAddingProductToCart}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>{t("updatingOrder")}</LoadingOverlay.Title>
      </LoadingOverlay.Root>
      <div css={styles.positionsContainer}>
        {(order.items || []).map((item) => (
          <OrderPosition key={item.id} cartItem={item} />
        ))}
      </div>
      <div css={styles.totals}>
        <Flex
          css={{
            marginTop: 10,
          }}
          alignItems={"center"}
        >
          <TextV2>{t("cartSummary.totalPositions")}:</TextV2>&nbsp;
          <TextV2 fontWeight={"semibold"}>{order.totalQuantity}</TextV2>
        </Flex>
        <Flex
          style={{
            height: 34,
          }}
          alignItems={"baseline"}
        >
          <TextV2>{t("cartSummary.totalSum")}:</TextV2>&nbsp;
          <H3>{formatPrice(order.total, Currency.EUR)}</H3>
        </Flex>
      </div>
      <Flex justifyContent="center" css={styles.footer}>
        <ButtonV2
          onClick={() => setIsOrderPreviewOpen(false)}
          skin={"info"}
          variant={"filled"}
        >
          {t("orderPreview.filledState.action")}
        </ButtonV2>
      </Flex>
    </>
  );
};
